import React from 'react'
import { useTranslation } from 'react-i18next'

import { Switch } from '@/lib/components'
import {
  AddCircleOutlineIcon,
  Button,
  EditableColumn,
  EditIcon,
  Radio,
  showToast,
  Skeleton,
  Text,
  Tooltip,
  TrashIcon,
} from '@mercai/clever'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency } from '@/helpers'
import { SectionHeader } from '@/pages/supply/supplier-order-management/components'
import { ProductQualityCategoryEnum } from '@/types'

import { ChainLink } from './components'
import { SelectSupplyOrderItemsProps } from './select-supply-order-items.type'
import styles from './select-supply-order-items.module.css'

export const SelectSupplyOrderItems = ({
  initialValues,
  values,
  setValues,
  disabled,
  onShowAddSaleOrders,
  isLoading,
  onRemove,
  onChangeValues,
  position,
  preview,
  errors,
  onRemoveChainLink,
  onRevertChainLink,
  markedAsRemoved,
  affectedSaleOrders = [],
  saleOrdersForGeneration = new Set(),
  onToggleSaleOrderForGeneration,
  onCreateNewSupplyOrder,
}: SelectSupplyOrderItemsProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const handleRemoveChainLink = (itemIndex: number, linkIndex: number) => {
    if (onRemoveChainLink) {
      onRemoveChainLink(itemIndex, linkIndex)
    }
  }

  const handleRevertChainLink = (itemIndex: number, linkIndex: number) => {
    if (onRevertChainLink) {
      onRevertChainLink(itemIndex, linkIndex)
    }
  }

  const handleChangeValues = async (
    key: string,
    index: number,
    value: string | number | boolean | undefined,
  ) => {
    let valueAux = value
    if (
      key === 'quantity' &&
      initialValues?.supplyOrderItems?.[index]?.quantity &&
      Number(value) < Number(initialValues?.supplyOrderItems?.[index]?.quantity)
    ) {
      showToast({
        variant: 'info',
        content: t('componentSelectSupplyOrderItemsSetQuantityWarning'),
      })
      valueAux = Number(initialValues?.supplyOrderItems?.[index]?.quantity)
    }

    await onChangeValues?.(index, key, valueAux)
  }

  const Loading = () => {
    return (
      <div className={styles['info-row-content']}>
        <div className={styles['sale-orders-container']}>
          {[0, 1].map((i) => (
            <div key={i} className={styles['products-card']}>
              <div className={styles['products-card-body']}>
                <div className={styles['products-card-body-item']}>
                  <Skeleton height="1rem" width="10rem" />
                </div>
                <div className={styles['products-card-body-item']}>
                  <Skeleton height="1rem" width="5rem" />
                </div>
                <div className={styles['products-card-body-item']}>
                  <Skeleton height="1rem" width="6rem" />
                </div>
                <div className={styles['products-card-body-item-total']}>
                  <Skeleton height="1rem" width="7rem" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={disabled ? styles.disabled : ''}>
      <SectionHeader
        title={`${position}. ${t('componentSelectSupplyOrderItemsTitle')}`}
      >
        <div className={styles['info-block']}>
          <div className={styles['info-row-content']}>
            <Text className={styles['info-item']}>
              {t('componentSelectSupplyOrderItemsCurrencyLabel')}
            </Text>

            <Radio
              value={values?.currency === 'MXN'}
              onChange={() =>
                setValues((prev) => ({ ...prev, currency: 'MXN' }))
              }
            >
              {t('componentSelectSupplyOrderItemsCurrencyMXNLabel')}
            </Radio>
            <Radio
              value={values?.currency === 'USD'}
              onChange={() =>
                setValues((prev) => ({ ...prev, currency: 'USD' }))
              }
            >
              {t('componentSelectSupplyOrderItemsCurrencyUSDLabel')}
            </Radio>
          </div>
          <div className={styles['sale-orders-header']}>
            <Button
              variant="ghost"
              className={styles['add-button']}
              onClick={onShowAddSaleOrders}
            >
              {t('componentSelectSupplyOrderItemsAddButton')}
              <AddCircleOutlineIcon />
            </Button>
          </div>
        </div>

        {isLoading && <Loading />}

        {!isLoading && (
          <div className={styles['info-row-content']}>
            <>
              {!values?.supplyOrderItems?.length ||
                values?.supplyOrderItems?.length === 0 ? (
                <div>
                  <Text className={styles['info-item']}>
                    {t('componentSelectSupplyOrderItemsEmptyTitle')}
                  </Text>
                  {errors?.items ? (
                    <div>
                      <Text className={styles.error}>
                        {errors.items as string}
                      </Text>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className={styles['sale-orders-container']}>
                  <div className={styles['products-card']}>
                    <div>
                      <div className={styles['products-card-body']}>
                        <div
                          className={styles['products-card-body-item-header']}
                        >
                          {t('generatePurchaseOrderProductColumn')}
                        </div>
                        <div
                          className={styles['products-card-body-item-header']}
                        >
                          {t('generatePurchaseOrderCostColumn')}
                          <Text>
                            {t('generatePurchaseOrderCostWithoutIvaColumn')}
                          </Text>
                        </div>
                        <div
                          className={styles['products-card-body-item-header']}
                        >
                          {t('generatePurchaseOrderQuantityColumn')}
                        </div>
                        <div
                          className={styles['products-card-body-item-header']}
                        >
                          {t('generatePurchaseOrderCategoryColumn')}
                        </div>
                        <div
                          className={
                            styles['products-card-body-item-header-total']
                          }
                        >
                          {t('generatePurchaseOrderTotalColumn')}
                          <Text>
                            {t('generatePurchaseOrderTotalWithoutIvaColumn')}
                          </Text>
                        </div>
                        {values?.supplyOrderItems?.map((item, index) => (
                          <React.Fragment key={index}>
                            <div className={styles['products-card-body-item']}>
                              <Tooltip
                                text={
                                  item?.catalogProduct?.attributes?.title || ''
                                }
                                className={styles.tooltip}
                              >
                                <Text className={styles['product-title']}>
                                  {item?.catalogProduct?.attributes?.title ||
                                    ''}
                                </Text>
                              </Tooltip>

                              <Button
                                variant="secondary"
                                model="round"
                                onClick={() => onRemove(index)}
                                className={styles['delete-button']}
                              >
                                <TrashIcon />
                              </Button>
                            </div>

                            <div className={styles['products-card-body-item']}>
                              {onChangeValues ? (
                                <EditableColumn
                                  value={item?.cost || ''}
                                  type="number"
                                  inputProps={{
                                    prefix: `${values?.currency} `,
                                    decimalPrecision: DECIMAL_PLACES_DEFAULT,
                                  }}
                                  onSubmit={async (value) =>
                                    await onChangeValues(index, 'cost', value)
                                  }
                                  hasTooltip={false}
                                  render={({ setEditing }) => (
                                    <>
                                      <p className={styles['values-box']}>
                                        <Text variant="text-small">
                                          {formatCurrency(
                                            preview?.items?.[index]?.cost
                                              ? Number(
                                                preview?.items?.[index]
                                                  ?.cost || 0,
                                              )
                                              : 0,
                                            'es-MX',
                                            values?.currency || 'MXN',
                                            DECIMAL_PLACES_DEFAULT,
                                          )}
                                        </Text>
                                        {` `}
                                        <Text
                                          variant="text-xsmall"
                                          className={styles['without-iva']}
                                        >
                                          {`(${formatCurrency(
                                            Number(
                                              preview?.items?.[index]
                                                ?.costWithoutIva || 0,
                                            ),
                                            'es-MX',
                                            values?.currency || 'MXN',
                                            DECIMAL_PLACES_DEFAULT,
                                          )})`}
                                        </Text>
                                      </p>
                                      <Button
                                        variant="secondary"
                                        model="default"
                                        size="small"
                                        onClick={() => setEditing(true)}
                                      >
                                        <EditIcon />
                                      </Button>
                                    </>
                                  )}
                                />
                              ) : (
                                <p className={styles['values-box']}>
                                  <Text variant="text-small">
                                    {formatCurrency(
                                      preview?.items?.[index]?.cost
                                        ? Number(
                                          preview?.items?.[index]?.cost || 0,
                                        )
                                        : 0,
                                      'es-MX',
                                      values?.currency || 'MXN',
                                      DECIMAL_PLACES_DEFAULT,
                                    )}
                                  </Text>
                                  {` `}
                                  <Text
                                    variant="text-xsmall"
                                    className={styles['without-iva']}
                                  >
                                    {`(${formatCurrency(
                                      Number(
                                        preview?.items?.[index]
                                          ?.costWithoutIva || 0,
                                      ),
                                      'es-MX',
                                      values?.currency || 'MXN',
                                      DECIMAL_PLACES_DEFAULT,
                                    )})`}
                                  </Text>
                                </p>
                              )}
                            </div>

                            <div className={styles['products-card-body-item']}>
                              {onChangeValues ? (
                                <EditableColumn
                                  value={item?.quantity || ''}
                                  type="number"
                                  inputProps={{
                                    decimalPrecision: 0,
                                    suffix: ' kg',
                                  }}
                                  onSubmit={async (value) =>
                                    await handleChangeValues(
                                      'quantity',
                                      index,
                                      value,
                                    )
                                  }
                                  hasTooltip={false}
                                  render={({ setEditing }) => (
                                    <>
                                      <Text
                                        variant="text-small"
                                        className={styles['values-box']}
                                      >
                                        {item?.quantity || '0'} kg
                                      </Text>
                                      <Button
                                        variant="secondary"
                                        model="default"
                                        size="small"
                                        onClick={() => setEditing(true)}
                                      >
                                        <EditIcon />
                                      </Button>
                                    </>
                                  )}
                                />
                              ) : (
                                <Text variant="text-small">
                                  {item?.quantity || '0'} kg
                                </Text>
                              )}
                            </div>

                            <div className={styles['products-card-body-item']}>
                              {onChangeValues ? (
                                <EditableColumn
                                  type="select"
                                  inputProps={{
                                    options: [
                                      {
                                        label: t('selectEmpty'),
                                        value: '',
                                      },
                                      ...Object.keys(
                                        ProductQualityCategoryEnum,
                                      ).map((key) => ({
                                        label: tEnums(
                                          `product_quality_category_${key}`,
                                        ),
                                        value: key,
                                      })),
                                    ],
                                  }}
                                  value={item?.category || ''}
                                  onSubmit={async (value) =>
                                    await handleChangeValues(
                                      'category',
                                      index,
                                      value,
                                    )
                                  }
                                  hasTooltip={false}
                                  render={({ setEditing }) => (
                                    <>
                                      <Text
                                        variant="text-small"
                                        className={styles['values-box']}
                                      >
                                        {item?.category
                                          ? tEnums(
                                            `product_quality_category_${item?.category}`,
                                          )
                                          : '-'}
                                      </Text>
                                      <Button
                                        variant="secondary"
                                        model="default"
                                        size="small"
                                        onClick={() => setEditing(true)}
                                      >
                                        <EditIcon />
                                      </Button>
                                    </>
                                  )}
                                />
                              ) : (
                                <Text variant="text-small">
                                  {item?.category
                                    ? tEnums(
                                      `product_quality_category_${item?.category}`,
                                    )
                                    : '-'}
                                </Text>
                              )}
                            </div>

                            <div
                              className={
                                styles['products-card-body-item-total']
                              }
                            >
                              <p className={styles['values-box-total']}>
                                <Text variant="title-base">
                                  {formatCurrency(
                                    preview?.items?.[index]?.total
                                      ? Number(
                                        preview?.items?.[index]?.total || 0,
                                      )
                                      : 0,
                                    'es-MX',
                                    values?.currency || 'MXN',
                                  )}
                                </Text>{' '}
                                <Text
                                  variant="text-small"
                                  className={styles['without-iva']}
                                >
                                  (
                                  {formatCurrency(
                                    preview?.items?.[index]?.totalWithoutIva
                                      ? Number(
                                        preview?.items?.[index]
                                          ?.totalWithoutIva || 0,
                                      )
                                      : 0,
                                    'es-MX',
                                    values?.currency || 'MXN',
                                  )}
                                  )
                                </Text>
                              </p>
                            </div>

                            {item?.supplyChainLink &&
                              item.supplyChainLink.length > 0 && (
                                <div className={styles['chain-link-container']}>
                                  {item?.supplyChainLink?.map(
                                    (link, linkIndex) => (
                                      <ChainLink
                                        key={linkIndex}
                                        value={link}
                                        itemIndex={index}
                                        linkIndex={linkIndex}
                                        onRemoveChainLink={
                                          handleRemoveChainLink
                                        }
                                        onRevertChainLink={
                                          handleRevertChainLink
                                        }
                                        isMarkedAsRemoved={Boolean(
                                          markedAsRemoved?.has(link.uuid || ''),
                                        )}
                                      />
                                    ),
                                  )}
                                </div>
                              )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          </div>
        )}

        {affectedSaleOrders.length > 0 && (
          <div className={styles['affected-sale-orders-section']}>
            <div className={styles['affected-sale-orders-header']}>
              <Text
                variant="title-medium"
                className={styles['affected-sale-orders-title']}
              >
                {t('componentSelectSupplyOrderItemsAffectedSaleOrdersTitle')}
              </Text>
              <Button
                variant="ghost"
                onClick={onCreateNewSupplyOrder}
                className={styles['add-button']}
                disabled={saleOrdersForGeneration.size === 0}
              >
                {t('componentSelectSupplyOrderItemsCreateNewSupplyOrderButton')}
                <AddCircleOutlineIcon />
              </Button>
            </div>

            <div className={styles['affected-sale-orders-list']}>
              {affectedSaleOrders.map((saleOrder) => (
                <>
                  <Text className={styles['affected-sale-order-item']}>
                    {saleOrder.buyer?.companyName || saleOrder.buyer?.name}
                  </Text>

                  <Text className={styles['affected-sale-order-item']}>
                    {saleOrder.reference}
                  </Text>

                  <div className={styles['affected-sale-order-item-switch']}>
                    <Switch
                      checked={saleOrdersForGeneration.has(saleOrder.uuid)}
                      onChange={(checked) =>
                        onToggleSaleOrderForGeneration?.(
                          saleOrder.uuid,
                          checked,
                        )
                      }
                      disabled={disabled}
                    />
                  </div>
                </>
              ))}
            </div>
          </div>
        )}
      </SectionHeader>
    </div>
  )
}
